import React, { useState, useEffect } from 'react';
import { TalentSelection, WeeklyLockingWithTalents } from '../services/weeklyLockInService';
import { useSubscription } from '../contexts/SubscriptionContext';
import { UNIFIED_TALENT_TREE } from '../data/unifiedTalentTree';

interface TalentSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (talents: TalentSelection[], notes?: string) => void;
  existingLocking?: WeeklyLockingWithTalents | null;
}

// Free user talents - only these 3 are available for free users
const FREE_USER_TALENTS = ['sleep-hygiene', 'stability-mastery', 'sunseeker'];

const TalentSelectionModal: React.FC<TalentSelectionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  existingLocking
}) => {
  const { isPremium } = useSubscription();
  const [selectedTalents, setSelectedTalents] = useState<TalentSelection[]>([]);
  const [notes, setNotes] = useState('');
  const [activeTab, setActiveTab] = useState<'longevity' | 'healthspan' | 'wellness'>('longevity');
  const [error, setError] = useState<string | null>(null);

  // Filter talents based on subscription status
  const availableTalents = React.useMemo(() => {
    return UNIFIED_TALENT_TREE.filter(talent => {
      // Exclude the root health-tree node
      if (talent.id === 'health-tree') return false;
      
      // For free users, only show the 3 allowed talents
      if (!isPremium) {
        return FREE_USER_TALENTS.includes(talent.id);
      }
      
      // Premium users see all talents
      return true;
    });
  }, [isPremium]);

  useEffect(() => {
    if (existingLocking) {
      // Pre-populate with existing selections
      const existing = existingLocking.locked_talents.map(talent => ({
        talent_id: talent.talent_id,
        talent_name: talent.talent_name,
        target_minutes_per_week: talent.target_minutes_per_week,
        tree_type: talent.tree_type
      }));
      setSelectedTalents(existing);
      setNotes(existingLocking.notes || '');
    }
  }, [existingLocking]);

  const handleTalentToggle = (talent: typeof UNIFIED_TALENT_TREE[0]) => {
    const isSelected = selectedTalents.some(t => t.talent_id === talent.id);
    
    if (isSelected) {
      setSelectedTalents(prev => prev.filter(t => t.talent_id !== talent.id));
    } else {
      const newTalent: TalentSelection = {
        talent_id: talent.id,
        talent_name: talent.name,
        target_minutes_per_week: talent.timeRequired,
        tree_type: talent.category
      };
      setSelectedTalents(prev => [...prev, newTalent]);
    }
    setError(null);
  };

  const handleMinutesChange = (talentId: string, minutes: number) => {
    setSelectedTalents(prev => 
      prev.map(talent => 
        talent.talent_id === talentId 
          ? { ...talent, target_minutes_per_week: minutes }
          : talent
      )
    );
  };

  const getTotalMinutes = () => {
    return selectedTalents.reduce((sum, talent) => sum + talent.target_minutes_per_week, 0);
  };

  const getTreeCounts = () => {
    const counts = { longevity: 0, healthspan: 0, wellness: 0 };
    selectedTalents.forEach(talent => {
      counts[talent.tree_type]++;
    });
    return counts;
  };

  const validateSelection = () => {
    if (selectedTalents.length < 3) {
      setError('You must select at least 3 talents.');
      return false;
    }

    const totalMinutes = getTotalMinutes();
    if (totalMinutes > 1500) {
      setError('Total time commitment cannot exceed 1500 minutes (25 hours) per week.');
      return false;
    }

    return true;
  };

  const handleSubmit = () => {
    if (validateSelection()) {
      onSubmit(selectedTalents, notes);
    }
  };

  const treeCounts = getTreeCounts();
  const totalMinutes = getTotalMinutes();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">
              {existingLocking ? 'Modify Weekly Commitment' : 'Create Weekly Commitment'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>
          <p className="text-gray-600 mt-2">
            Select 3+ talents from any tree. Maximum 1500 minutes (25 hours) per week.
          </p>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Tab Navigation */}
          <div className="flex border-b mb-6">
            {(['longevity', 'healthspan', 'wellness'] as const).map(tree => (
              <button
                key={tree}
                onClick={() => setActiveTab(tree)}
                className={`px-4 py-2 font-medium capitalize relative ${
                  activeTab === tree
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tree}
                {treeCounts[tree] > 0 && (
                  <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {treeCounts[tree]}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Talent Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {availableTalents
              .filter(talent => talent.category === activeTab)
              .map(talent => {
                const isSelected = selectedTalents.some(t => t.talent_id === talent.id);
                const selectedTalent = selectedTalents.find(t => t.talent_id === talent.id);
                
                return (
                  <div
                    key={talent.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleTalentToggle(talent)}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-semibold text-gray-900">{talent.name}</h3>
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                      }`}>
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{talent.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        {talent.category}
                      </span>
                      {isSelected && selectedTalent && (
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            min="5"
                            max="120"
                            value={selectedTalent.target_minutes_per_week}
                            onChange={(e) => handleMinutesChange(talent.id, parseInt(e.target.value) || 0)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-16 px-2 py-1 border rounded text-sm"
                          />
                          <span className="text-sm text-gray-600">min/week</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Premium Upgrade Section for Free Users */}
          {!isPremium && (
            <div className="mb-6 p-4 border border-orange-200 rounded-lg bg-orange-50">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-orange-600 font-semibold">🔒 Free Plan Limitations</span>
              </div>
              <p className="text-sm text-orange-700 mb-3">
                You currently have access to {FREE_USER_TALENTS.length} foundation talents. 
                Upgrade to Premium to unlock all {UNIFIED_TALENT_TREE.length - 1} talents and advanced features.
              </p>
              <button 
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
                onClick={() => window.open('/pricing', '_blank')}
              >
                Upgrade to Premium
              </button>
            </div>
          )}

          {/* Notes Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about your weekly goals..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Summary */}
          <div className="bg-blue-50 rounded-lg p-4 mb-4">
            <h3 className="font-semibold text-gray-900 mb-2">Selection Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Total Talents:</span>
                <span className={`ml-2 font-medium ${selectedTalents.length >= 3 ? 'text-green-600' : 'text-red-600'}`}>
                  {selectedTalents.length}/3 min
                </span>
              </div>
              <div>
                <span className="text-gray-600">Total Time:</span>
                <span className={`ml-2 font-medium ${totalMinutes <= 1500 ? 'text-green-600' : 'text-red-600'}`}>
                  {totalMinutes}/1500 min
                </span>
              </div>
              <div>
                <span className="text-gray-600">Trees Used:</span>
                <span className={`ml-2 font-medium text-blue-600`}>
                  {Object.values(treeCounts).filter(c => c > 0).length} trees
                </span>
              </div>
              <div>
                <span className="text-gray-600">Avg/Day:</span>
                <span className="ml-2 font-medium text-gray-900">
                  {Math.round(totalMinutes / 7)} min
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={selectedTalents.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {existingLocking ? 'Update Commitment' : 'Create Commitment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TalentSelectionModal; 